/**
 * Bezier Waves Alpine.js Component
 * Interactive canvas background with spring-physics-driven wave lines
 */

import { observeFooterReveal } from './utils/footer-reveal.js';

export default () => ({
    // Configuration
    LINE_COUNT: 22,
    LINE_SPACING: 16,
    POINTS_PER_LINE: 100,
    BASE_AMPLITUDE: 10,
    LEFT_MARGIN: 40,
    RIGHT_MARGIN: 0,
    DIP_AMOUNT: 0.12,
    LIFT_AMOUNT: 0.35,
    MOUSE_RADIUS: 280,
    MOUSE_STRENGTH: 90,
    SPRING_STIFFNESS: 0.022,
    DAMPING: 0.91,
    NEIGHBOUR_COUPLING: 0.012,

    // Ribbon appearance
    RIBBON_WIDTH: 1.25,

    // Gradient colors in drawing space; on screen they're mirrored (see resize())
    COLOR_LEFT:  { h: 235, s: 100, l: 72 },  // --color-secondary
    COLOR_RIGHT: { h: 94,  s: 100, l: 42 },  // --color-primary

    // Lighting
    LIGHT_DIR: { x: -0.3, y: -0.8, z: 0.5 },
    AMBIENT: 0.45,
    DIFFUSE_STRENGTH: 0.55,
    SPECULAR_STRENGTH: 0.7,
    SPECULAR_POWER: 24,

    // State
    canvas: null,
    ctx: null,
    width: 0,
    height: 0,
    dpr: 1,
    lines: [],
    mouse: { x: -9999, y: -9999, active: false },
    animationId: null,
    resizeHandler: null,
    prefersReducedMotion: false,

    init() {
        if (!window.matchMedia('(min-width: 768px)').matches) return;

        this.canvas = this.$refs.canvas;
        if (!this.canvas) return;

        this.ctx = this.canvas.getContext('2d');
        if (!this.ctx) return;

        this.dpr = window.devicePixelRatio || 1;
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Pre-compute normalized light direction
        const ld = this.LIGHT_DIR;
        const lLen = Math.sqrt(ld.x * ld.x + ld.y * ld.y + ld.z * ld.z);
        this.lx = ld.x / lLen;
        this.ly = ld.y / lLen;
        this.lz = ld.z / lLen;

        // Bind event handlers
        this.resizeHandler = this.resize.bind(this);
        // Pointer x is mirrored to match the flipped canvas transform (see resize()).
        this._onMouseMove = (e) => {
            const rect = this.canvas.getBoundingClientRect();
            this.mouse.x = rect.right - e.clientX;
            this.mouse.y = e.clientY - rect.top;
            this.mouse.active = true;
        };
        this._onMouseLeave = () => { this.mouse.active = false; };
        this._onTouchMove = (e) => {
            // Don't preventDefault: the canvas fills the footer (min-h-dvh), so
            // swallowing touchmove would block page scrolling on touch devices.
            const touch = e.touches[0];
            const rect = this.canvas.getBoundingClientRect();
            this.mouse.x = rect.right - touch.clientX;
            this.mouse.y = touch.clientY - rect.top;
            this.mouse.active = true;
        };
        this._onTouchEnd = () => { this.mouse.active = false; };

        // Attach listeners
        window.addEventListener('resize', this.resizeHandler);
        this.canvas.addEventListener('mousemove', this._onMouseMove);
        this.canvas.addEventListener('mouseleave', this._onMouseLeave);
        this.canvas.addEventListener('touchmove', this._onTouchMove, { passive: true });
        this.canvas.addEventListener('touchend', this._onTouchEnd);

        this.resize();
        this.$el.classList.remove('opacity-0');

        if (this.prefersReducedMotion) {
            this.draw();
        } else {
            this.stopObservingFooter = observeFooterReveal((revealed) => revealed ? this.start() : this.stop());
        }
    },

    start() {
        if (!this.animationId) this.loop();
    },

    stop() {
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
    },

    destroy() {
        this.stopObservingFooter?.();
        this.stop();
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
        }
        if (this.canvas) {
            this.canvas.removeEventListener('mousemove', this._onMouseMove);
            this.canvas.removeEventListener('mouseleave', this._onMouseLeave);
            this.canvas.removeEventListener('touchmove', this._onTouchMove);
            this.canvas.removeEventListener('touchend', this._onTouchEnd);
        }
    },

    resize() {
        const parent = this.canvas.parentElement;
        this.width = parent.clientWidth;
        this.height = parent.clientHeight;
        this.canvas.width = this.width * this.dpr;
        this.canvas.height = this.height * this.dpr;
        this.canvas.style.width = this.width + 'px';
        this.canvas.style.height = this.height + 'px';
        // Mirrored horizontally so the cluster and fade sit on the right. Everything below
        // is drawn as if they were on the left, so COLOR_LEFT lands on the right; pointer x is mirrored to match.
        this.ctx.setTransform(-this.dpr, 0, 0, this.dpr, this.width * this.dpr, 0);
        this.initLines();
    },

    initLines() {
        this.lines = [];

        const leftClusterTop = this.height * 0.2 - ((this.LINE_COUNT - 1) * this.LINE_SPACING) / 2;
        const leftSpacing = this.LINE_SPACING;
        const rightSpacing = (this.height - this.RIGHT_MARGIN * 2) / (this.LINE_COUNT - 1);

        for (let i = 0; i < this.LINE_COUNT; i++) {
            const leftY = leftClusterTop + i * leftSpacing;
            const rightY = this.RIGHT_MARGIN + i * rightSpacing;
            const points = [];

            const phase1 = i * 0.7 + 0.3;
            const phase2 = i * 1.1 + 0.8;
            const phase3 = i * 0.4 + 2.1;

            const travel = leftY - rightY;
            const p0 = leftY;
            const p1 = leftY + Math.abs(travel) * this.DIP_AMOUNT;
            const p2 = rightY + travel * this.LIFT_AMOUNT;
            const p3 = rightY;

            for (let j = 0; j < this.POINTS_PER_LINE; j++) {
                const t = j / (this.POINTS_PER_LINE - 1);
                const x = t * this.width;

                const mt = 1 - t;
                const baseY = mt*mt*mt*p0 + 3*mt*mt*t*p1 + 3*mt*t*t*p2 + t*t*t*p3;

                const spacingAtT = leftSpacing + (rightSpacing - leftSpacing) * t;
                const maxRipple = spacingAtT * 0.25;
                const rippleScale = maxRipple / this.BASE_AMPLITUDE;

                const wave1 = Math.sin(t * Math.PI * 2.5 + phase1) * this.BASE_AMPLITUDE * rippleScale * 0.55;
                const wave2 = Math.sin(t * Math.PI * 4.3 + phase2) * this.BASE_AMPLITUDE * rippleScale * 0.28;
                const wave3 = Math.sin(t * Math.PI * 1.2 + phase3) * this.BASE_AMPLITUDE * rippleScale * 0.17;
                const restY = baseY + wave1 + wave2 + wave3;

                points.push({ x, restY, y: restY, vy: 0 });
            }

            // White lines with ~50% transparency
            const hue = 0;
            const saturation = 0;
            const lightness = 100;
            const alpha = 0.22 + Math.sin(i * 0.45) * 0.05;

            this.lines.push({ points, hue, saturation, lightness, alpha });
        }
    },

    update() {
        // Cache reactive properties to avoid Alpine proxy overhead in hot loop
        const lines = this.lines;
        const mouse = this.mouse;
        const mouseRadius = this.MOUSE_RADIUS;
        const radiusSq = mouseRadius * mouseRadius;
        const stiffness = this.SPRING_STIFFNESS;
        const damping = this.DAMPING;
        const coupling = this.NEIGHBOUR_COUPLING;

        for (const line of lines) {
            const pts = line.points;
            const len = pts.length;

            for (let i = 0; i < len; i++) {
                const pt = pts[i];
                let targetY = pt.restY;

                if (mouse.active) {
                    const dx = mouse.x - pt.x;
                    const dy = mouse.y - pt.restY;
                    const distSq = dx * dx + dy * dy;

                    if (distSq < radiusSq) {
                        const dist = Math.sqrt(distSq);
                        const t = 1 - dist / mouseRadius;
                        const influence = t * t * t;
                        targetY = pt.restY + dy * influence * 0.92;
                    }
                }

                const displacement = pt.y - targetY;
                pt.vy -= displacement * stiffness;

                if (i > 0) {
                    pt.vy += (pts[i - 1].y - pt.y) * coupling;
                }
                if (i < len - 1) {
                    pt.vy += (pts[i + 1].y - pt.y) * coupling;
                }

                pt.vy *= damping;
                pt.y += pt.vy;
            }
        }
    },

    // Reusable lighting result to avoid allocations in hot loop
    _lighting: { diffuse: 0, specular: 0 },

    computeSegmentLighting(p0, p1, lx, ly, lz, ambient, diffStr, specStr, specPow, out) {
        const dx = p1.x - p0.x;
        const dy = p1.y - p0.y;
        const segLen = Math.sqrt(dx * dx + dy * dy);
        if (segLen < 0.001) { out.diffuse = ambient; out.specular = 0; return; }

        const ty = dy / segLen;
        const nx = -ty;

        const slope = ty;
        const nz = Math.sqrt(Math.max(0, 1 - slope * slope * 0.8));
        const surfNx = nx * Math.abs(slope) * 0.5;
        const surfNy = slope * 0.6;
        const surfNz = nz;

        const snLen = Math.sqrt(surfNx * surfNx + surfNy * surfNy + surfNz * surfNz);
        const snx = surfNx / snLen;
        const sny = surfNy / snLen;
        const snz = surfNz / snLen;

        const diffDot = Math.max(0, snx * lx + sny * ly + snz * lz);
        out.diffuse = ambient + diffDot * diffStr;

        const reflDot = snx * lx + sny * ly + snz * lz;
        const reflZ = lz - 2 * reflDot * snz;
        const specDot = Math.max(0, reflZ);
        out.specular = Math.pow(specDot, specPow) * specStr;
    },

    drawRibbon(line, ctx, ribbonWidth, lx, ly, lz, ambient, diffStr, specStr, specPow, cL, cR, w, startIdx) {
        const pts = line.points;
        const len = pts.length;
        if (len < 2) return;

        // Pre-compute color interpolation deltas
        const invW = 1 / w;
        const dh = cR.h - cL.h;
        const ds = cR.s - cL.s;
        const dl = cR.l - cL.l;
        const baseAlpha = line.alpha;
        const alphaRange = 1 - baseAlpha;

        // Compute normals, edge points, and draw quads in a single pass
        // Reuse a single lighting object to avoid allocations
        const lit = this._lighting;
        // Pre-compute first normal
        const tdx0 = pts[Math.min(1, len - 1)].x - pts[0].x;
        const tdy0 = pts[Math.min(1, len - 1)].y - pts[0].y;
        const tLen0 = Math.sqrt(tdx0 * tdx0 + tdy0 * tdy0) || 1;
        let curNx = -tdy0 / tLen0;
        let curNy = tdx0 / tLen0;
        let curUx = pts[0].x + curNx * ribbonWidth;
        let curUy = pts[0].y + curNy * ribbonWidth;
        let curLx = pts[0].x - curNx * ribbonWidth;
        let curLy = pts[0].y - curNy * ribbonWidth;

        // Draw filled quads with per-segment lighting and color gradient
        for (let i = startIdx; i < len - 1; i++) {
            const p = pts[i];
            const pNext = pts[i + 1];

            // Current edge points (reuse from previous iteration or initial)
            const u0x = curUx, u0y = curUy;
            const l0x = curLx, l0y = curLy;

            // Compute next normal
            const prev = pts[Math.max(i, 0)];
            const next = pts[Math.min(i + 2, len - 1)];
            const tdx = next.x - prev.x;
            const tdy = next.y - prev.y;
            const tLen = Math.sqrt(tdx * tdx + tdy * tdy) || 1;
            curNx = -tdy / tLen;
            curNy = tdx / tLen;
            curUx = pNext.x + curNx * ribbonWidth;
            curUy = pNext.y + curNy * ribbonWidth;
            curLx = pNext.x - curNx * ribbonWidth;
            curLy = pNext.y - curNy * ribbonWidth;

            // Lighting
            this.computeSegmentLighting(p, pNext, lx, ly, lz, ambient, diffStr, specStr, specPow, lit);
            const brightness = Math.min(1, lit.diffuse);
            const spec = lit.specular;

            // Color interpolation
            const t = p.x * invW;
            const h = cL.h + dh * t;
            const s = cL.s + ds * t;
            const bl = cL.l + dl * t;
            const l = bl * brightness;
            const alpha = baseAlpha + alphaRange * t;

            // Draw quad
            ctx.beginPath();
            ctx.moveTo(u0x, u0y);
            ctx.lineTo(curUx, curUy);
            ctx.lineTo(curLx, curLy);
            ctx.lineTo(l0x, l0y);
            ctx.closePath();

            ctx.fillStyle = `hsla(${h}, ${s}%, ${l}%, ${alpha})`;
            ctx.fill();

            if (spec > 0.02) {
                ctx.fillStyle = `rgba(200, 220, 255, ${spec * alpha * 0.8})`;
                ctx.fill();
            }
        }
    },

    draw() {
        // Cache reactive properties to avoid Alpine proxy overhead
        const ctx = this.ctx;
        const lines = this.lines;
        const ribbonWidth = this.RIBBON_WIDTH;
        const lx = this.lx;
        const ly = this.ly;
        const lz = this.lz;
        const ambient = this.AMBIENT;
        const diffStr = this.DIFFUSE_STRENGTH;
        const specStr = this.SPECULAR_STRENGTH;
        const specPow = this.SPECULAR_POWER;
        const cL = this.COLOR_LEFT;
        const cR = this.COLOR_RIGHT;

        const w = this.width;
        const h = this.height;

        ctx.clearRect(0, 0, w, h);

        // Skip segments that would be fully erased by the left fade mask.
        // The destination-out gradient runs from x=0 (full erase) to x=w*0.5 (no erase).
        // Segments below ~5% of width are nearly invisible, so skip them.
        const fadeEnd = w * 0.5;
        const skipThreshold = w * 0.05;
        const pointsPerLine = lines[0] ? lines[0].points.length : 0;
        const startIdx = pointsPerLine > 0 ? Math.max(0, Math.floor((skipThreshold / w) * (pointsPerLine - 1))) : 0;

        for (const line of lines) {
            this.drawRibbon(line, ctx, ribbonWidth, lx, ly, lz, ambient, diffStr, specStr, specPow, cL, cR, w, startIdx);
        }

        // Fade ribbons out toward the left edge using destination-out
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        const grad = ctx.createLinearGradient(0, 0, fadeEnd, 0);
        grad.addColorStop(0, 'rgba(0, 0, 0, 1)');
        grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, fadeEnd, h);
        ctx.restore();
    },

    loop() {
        this.update();
        this.draw();
        this.animationId = requestAnimationFrame(() => this.loop());
    },
});
